import React, {useState, useRef} from 'react'
import {
    Heading,
    Stack,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    useBreakpointValue,
    Image,
    useStyleConfig,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import ColorRefinements from './color-refinements'
import SizeRefinements from './size-refinements'
import RadioRefinements from './radio-refinements'
import CheckboxRefinements from './checkbox-refinements'
import {getUrl} from '../../../utils/cloudinary-utils'
import StoreRefinement from './store-refinement'
import {BM_FILTER_MAPPING} from '../../../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSiteContext} from '../../../contexts/site'

export const componentMap = {
    c_stoneColor: ColorRefinements,
    c_metal: ColorRefinements,
    metal: ColorRefinements,
    Metal: ColorRefinements,
    Color: ColorRefinements,
    c_colorGeneral: ColorRefinements,
    'Ring Size': SizeRefinements,
    c_size: SizeRefinements,
    price: RadioRefinements,
    Price: RadioRefinements
}

const DrawerRefinements = ({
    itemsBefore,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    inStockAt,
    selectedFilters,
    isMenu = true,
    isLoading
}) => {
    const {isYR} = useSiteContext()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {storeFilterConfig} = commerceAPI.storefront
    var yrEnableStoreFilter = false,
        ksEnableStoreFilter = false
    try {
        if (storeFilterConfig) {
            yrEnableStoreFilter = JSON.parse(storeFilterConfig).yrEnableStoreFilter
            ksEnableStoreFilter = JSON.parse(storeFilterConfig).ksEnableStoreFilter
        }
    } catch (e) {
        console.error(e)
    }

    const accordionRef = useRef(null)
    const styles = useStyleConfig('accordion')
    const productListStyles = useStyleConfig('ProductListStyles')
    const isDesktop = useBreakpointValue({base: false, lg: true})

    if (excludedFilters) {
        filters = filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
    }

    let filtersIndexes = filters?.map((filter, idx) => idx)
    const [values, setValues] = useState([])
    const selectedIndex = values
        ?.map((v) => filters.filter((f) => f.values).findIndex((f) => f.attributeId === v))
        .filter((i) => i !== -1)

    return (
        <Stack
            spacing={8}
            className="refinements-wrapper"
            display="block"
            position="relative"
            width="100%"
        >
            {(isYR && yrEnableStoreFilter) || (!isYR && ksEnableStoreFilter) ? (
                <StoreRefinement selectedFilters={selectedFilters} inStockAt={inStockAt} />
            ) : (
                <></>
            )}
            {filtersIndexes && (
                <Accordion
                    pointerEvents={isLoading ? 'none' : 'auto'}
                    ref={accordionRef}
                    display="contents"
                    flexWrap="wrap"
                    allowMultiple={true}
                    variant="plain"
                    index={selectedIndex}
                    onChange={(index) => {
                        var attributeIds = index.map(
                            (i) => filters?.filter(({values}) => values)?.[i]?.attributeId
                        )
                        setValues(attributeIds)
                    }}
                >
                    {itemsBefore}
                    {filters?.map((filter, idx) => {
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []
                        const validValues = filter?.values?.map((v) => v.value)
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        } else {
                            selectedFiltersArray = selectedFiltersArray?.filter((val) =>
                                validValues.includes(val)
                            )
                        }

                        if (filter.values) {
                            const allZero = filter.values.every((item) => item.hitCount === 0)
                            if (allZero) {
                                return <></>
                            }
                            return (
                                <Stack
                                    key={filter.attributeId + idx}
                                    divider={<Divider />}
                                    margin="0 0 30px 0"
                                    alignContent="center"
                                    display="block"
                                >
                                    <AccordionItem
                                        border="none"
                                        position="relative"
                                        value={filter.attributeId}
                                    >
                                        {({isExpanded}) => (
                                            <>
                                                <AccordionButton padding={0} margin="0">
                                                    <Heading
                                                        as="p"
                                                        flex="1"
                                                        data-expanded={isExpanded}
                                                        fontWeight="400"
                                                        fontSize={{
                                                            md: '23px',
                                                            base: '16px'
                                                        }}
                                                        onClick={() => {
                                                            setValues(filter.attributeId)
                                                        }}
                                                        textAlign="left"
                                                        fontFamily={{base: 'body', md: 'heading'}}
                                                        color="black.100"
                                                        minHeight="29px"
                                                        whiteSpace="nowrap"
                                                        display="flex"
                                                        data-label={filter.label}
                                                        label={filter.label}
                                                        alignItems="center"
                                                    >
                                                        {BM_FILTER_MAPPING?.[filter.label] ||
                                                            filter.label}
                                                        {selectedFiltersArray?.length && isDesktop
                                                            ? ' (' +
                                                              selectedFiltersArray?.length +
                                                              ')'
                                                            : ''}
                                                        {!isDesktop &&
                                                        selectedFiltersArray?.length ? (
                                                            <Box
                                                                display="block"
                                                                marginLeft="20px"
                                                                backgroundColor="gray.50"
                                                                borderRadius="50%"
                                                                fontSize="13px"
                                                                fontFamily="body"
                                                                alignContent="center"
                                                                width="29px"
                                                                height="29px"
                                                                textAlign="center"
                                                            >
                                                                {selectedFiltersArray?.length}
                                                            </Box>
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </Heading>
                                                    {/* Icon Container */}
                                                    <div
                                                        style={{
                                                            transform: isExpanded
                                                                ? 'rotate(180deg)'
                                                                : 'rotate(0deg)',
                                                            display: 'flex',
                                                            margin: '0',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '13px',
                                                            height: '13px'
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <Image
                                                                alt="Expand Icon"
                                                                width="13px"
                                                                height="13px"
                                                                src={getUrl(
                                                                    'UIImages/images/minus-icon.svg'
                                                                )}
                                                                className="accordion-action"
                                                                {...productListStyles.body
                                                                    .refinements.arrowImage}
                                                            />
                                                        ) : (
                                                            <Image
                                                                alt="Expand Icon"
                                                                width="13px"
                                                                height="13px"
                                                                src={getUrl(
                                                                    'UIImages/images/plus-icon.svg'
                                                                )}
                                                                className="accordion-action"
                                                                {...productListStyles.body
                                                                    .refinements.arrowImage}
                                                            />
                                                        )}
                                                    </div>
                                                </AccordionButton>
                                                {/* Accordion Panel with transition */}
                                                <AccordionPanel
                                                    position="unset"
                                                    fontSize="23px"
                                                    fontWeight="400"
                                                    sx={{
                                                        '& .chakra-checkbox__control svg': {
                                                            stroke: 'purple.100 !important'
                                                        }
                                                    }}
                                                    borderRadius="4px"
                                                    border={0}
                                                    borderColor="gray.500 !important"
                                                    padding={{
                                                        base: '0 0 10px 0rem',
                                                        md: '20px 0 0 0'
                                                    }}
                                                    style={{
                                                        transition:
                                                            'height 0.3s ease, opacity 0.3s ease',
                                                        height: isExpanded ? 'auto' : '0',
                                                        opacity: isExpanded ? '1' : '0',
                                                        overflow: 'hidden'
                                                    }}
                                                    paddingTop="0"
                                                    paddingBottom="0"
                                                    left="0"
                                                    right="0"
                                                    top="167%"
                                                    zIndex="5"
                                                    minWidth={
                                                        filter.attributeId === 'c_size' ||
                                                        filter.attributeId === 'Size' ||
                                                        filter.attributeId === 'c_RingSize' ||
                                                        filter.attributeId === 'Ring Size'
                                                            ? isDesktop
                                                                ? '372px'
                                                                : '100%'
                                                            : 'auto'
                                                    }
                                                    paddingLeft="0"
                                                    background="white"
                                                    {...styles.pannel}
                                                >
                                                    {isExpanded && (
                                                        <Values
                                                            selectedFilters={selectedFiltersArray}
                                                            filter={filter}
                                                            isMenu={isMenu}
                                                            toggleFilter={toggleFilter}
                                                        />
                                                    )}
                                                </AccordionPanel>
                                            </>
                                        )}
                                    </AccordionItem>
                                </Stack>
                            )
                        } else {
                            return null
                        }
                    })}
                </Accordion>
            )}
        </Stack>
    )
}

DrawerRefinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    inStockAt: PropTypes.any,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    isMenu: PropTypes.bool,
    onOpen: PropTypes.func
}

export default DrawerRefinements
